import React from 'react'

import InfiniteScroll from 'react-infinite-scroll-component'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'

import { CardGridWithInfinteScroll } from '@components/UI/CardGrid'
import Loading from '@components/UI/Loading'
import { NoticeEmpty } from '@components/UI/Notice'
import { withRememberScrollPosition } from '@hocs'
import { IApplicationState } from '@store'
import {
  fetchShareableContentLibrary,
  selectMediaObjectIsFetching,
  selectShareableContentLibraryHasMore,
  selectShareableContentLibraryMediaObjectIds,
} from '@store/mediaObjects'
import { Container } from '@tribegroup'
import { isEmpty } from '@utils'
import ShareableContentLibraryCard from './Card'

interface IParams {
  token: string
}

export const ShareableContentLibraryList: React.FC = () => {
  const dispatch = useDispatch()
  const { token } = useParams<IParams>()
  const isFetching = useSelector((state: IApplicationState) => selectMediaObjectIsFetching(state))
  const mediaObjectIds = useSelector((state: IApplicationState) =>
    selectShareableContentLibraryMediaObjectIds(state),
  )
  const hasMore = useSelector((state: IApplicationState) =>
    selectShareableContentLibraryHasMore(state),
  )
  const isInitialFetch = isFetching && isEmpty(mediaObjectIds)
  const hasEmptyList = !isFetching && isEmpty(mediaObjectIds)

  React.useEffect(() => {
    dispatch(fetchShareableContentLibrary(token))
  }, [])

  if (isInitialFetch) {
    return <Loading height={5} />
  }

  if (hasEmptyList) {
    return (
      <NoticeEmpty
        topOuterSpacing={7}
        alt="shareable-content-library"
        title={<FormattedMessage id="contentLibrary.shareable.empty.title" />}
      />
    )
  }

  const fetchNext = () => {
    dispatch(fetchShareableContentLibrary(token, true))
  }

  return (
    <Container topOuterSpacing={3.75} width="85vw">
      <CardGridWithInfinteScroll gap={0.9375} noSideBar>
        <InfiniteScroll
          dataLength={mediaObjectIds.length}
          next={fetchNext}
          loader={<Loading height={30} />}
          hasMore={hasMore}
          scrollThreshold={0.8}
          style={{ overflow: 'hidden' }}
        >
          <div>
            {mediaObjectIds.map((mediaObjectId) => (
              <ShareableContentLibraryCard key={mediaObjectId} mediaObjectCardId={mediaObjectId} />
            ))}
          </div>
        </InfiniteScroll>
      </CardGridWithInfinteScroll>
    </Container>
  )
}

export default withRememberScrollPosition(ShareableContentLibraryList)
